import React, { useContext, useEffect, useState } from "react";
import Grid from '@mui/material/Grid2';
import { WithGrow } from "@common/components/WithGrow";
import Controls, { getSortOptionsFn } from "../common/Controls";
import { LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MeetingElog, MeetingRevision } from "@services/meetings/types";
import { getMeetingRevisions } from "@services/meetings";
import { canEditElogsAndRevisions } from "@common/utils/user";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import Card, { CardHeader } from "../common/Card";
import LabeledText, { LabeledElementGroup } from "../common/LabeledText";
import { SortDirections, SortObject } from "../common/Controls/types";
import { AttachMoney, Money } from "@mui/icons-material";
import { displayDateTime } from "@common/utils/localTime.utils";
enum SortKeys {
  DATE = "Date",
  USER = "User",
  BILLABLE = "Billable",
  DESCRIPTION = "Description",
}
const getSortOptions = getSortOptionsFn([
  SortKeys.DATE,
  SortKeys.USER,
  SortKeys.BILLABLE,
  SortKeys.DESCRIPTION,
]);

export const getSortFn =
  ({ key, direction }: SortObject<SortKeys>) =>
  (a: MeetingRevision, b: MeetingRevision) => {
    let valueA: string | number, valueB: string | number;
    const sign = direction === SortDirections.ASC ? 1 : -1;
    switch (key) {
      case SortKeys.DATE:
        valueA = a.created;
        valueB = b.created;
        return (valueA - valueB) * sign;
      case SortKeys.BILLABLE:
        valueA = a.billable ? 1 : 0;
        valueB = b.billable ? -1 : 0;
        return (valueA - valueB) * sign;
      case SortKeys.USER:
        valueA = a.creator ?? "";
        valueB = b.creator ?? "";
        break;
      case SortKeys.DESCRIPTION:
        valueA = a.description ?? "";
        valueB = b.description ?? "";
        break;
    }

    return valueA.localeCompare(valueB) * sign;
  };

const Revisions = () => {
  const context = useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const meeting = { ...context.detail?.meeting };
  const canEdit = canEditElogsAndRevisions(user);
  const { t } = useTranslation();
  const dispatchError = useErrorNotification();

  const [sortIndex, setSortIndex] = useState<number>(0);
  const [revisions, setRevisions] = useState<MeetingRevision[]>();
  const [loading, setLoading] = useState<boolean>(false);

  const refetch = async (id: typeof meeting.meetingId) => {
    try {
      setLoading(true);
      setRevisions(await getMeetingRevisions(id));
    } catch (e) {
      dispatchError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (meeting?.id !== undefined) refetch(meeting.meetingId);
  }, [context.detail?.meeting?.id]);

  const sortOptions = getSortOptions(t);

  return (
    <WithGrow
      direction="column"
      fixed={
        <Controls
          options={sortOptions.map(({ label }, value) => ({ label, value }))}
          selected={sortIndex}
          onSelect={setSortIndex}
          onRefresh={() => refetch(meeting.meetingId)}
        />
      }
      containerSx={{ overflow: "hidden" }}
    >
      <Grid
        container
        direction="column"
        wrap="nowrap"
        sx={{ width: '100%', overflowY: "scroll",
          height: "100%",
          py: 0.5, }}
      >
        {meeting === undefined || revisions === undefined || loading ? (
          <LinearProgress />
        ) : (
          revisions
            .sort(getSortFn(sortOptions[sortIndex].value))
            .map((revision) => (
              <Card key={revision.id}>
                <CardHeader>
                  <LabeledElementGroup
                    items={[
                      {
                        label: "Date",
                        value: displayDateTime(revision.created),
                        flex: 3,
                      },
                      {
                        label: "User",
                        value: revision.creator || "-",
                        flex: 3,
                      },
                      {
                        label: "Billable",
                        value: revision.billable ? (
                          <AttachMoney sx={{ alignSelf: "center" }} />
                        ) : (
                          ""
                        ),
                      },
                    ]}
                  />
                </CardHeader>
                <LabeledText
                  label="Description"
                  value={revision.description}
                  fullText
                />
              </Card>
            ))
        )}
      </Grid>
    </WithGrow>
  );
};

export default Revisions;
